import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { gql } from "~/__generated__"
import { JourneyLogDisplay } from "~/__generated__/graphql"
import { useViewer } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import { directImageUpload } from "~/common/direct-image-upload"
import { dashboardPath, puzzleLabelPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { Button } from "~/ui/button"
import { PhotoDropzone } from "~/ui/photo-dropzone"

const puzzleCreateMutation = gql(/* GraphQL */ `
  mutation PuzzleCreate($input: PuzzleCreateInput!) {
    puzzleCreate(input: $input) {
      puzzle {
        id
        wpId
      }
    }
  }
`)

const formSchema = z
  .object({
    title: z.string().min(1, "Title is required."),
    brand: z.string().min(1, "Brand is required."),
    pieceCount: z
      .number({ message: "Enter the piece count." })
      .int("Whole numbers only.")
      .positive("Must be greater than zero.")
      .max(100_000),
    completedOn: z.string().min(1, "Pick a date."),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    comment: z.string().max(500, "Comment must be 500 characters or fewer.").optional(),
    passedOn: z.boolean(),
    nextLocation: z.string().optional(),
    displayAs: z.enum(["FIRST_NAME", "ANONYMOUS"]),
  })
  .refine((data) => Boolean(data.city?.trim()) || Boolean(data.zipCode?.trim()), {
    message: "Please enter a city or zip code.",
    path: ["city"],
  })

type FormValues = z.infer<typeof formSchema>

const inputClassName =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-50"

const textareaClassName =
  "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

export const RegisterPuzzleScreen = () => {
  useDocumentTitle("Register a puzzle")
  const navigate = useNavigate()
  const { viewer } = useViewer()
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [logPhotoFile, setLogPhotoFile] = useState<File | null>(null)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      brand: "",
      pieceCount: 1000,
      completedOn: new Date().toISOString().slice(0, 10),
      city: "",
      state: "",
      zipCode: "",
      comment: "",
      passedOn: false,
      nextLocation: "",
      displayAs: viewer.defaultDisplayAs ?? "FIRST_NAME",
    },
  })

  const [exec, { loading: submitting }] = useMutation(puzzleCreateMutation)

  const onSubmit = async (values: FormValues) => {
    if (!photoFile) {
      toast.error("Photo required", {
        description: "Pick a photo of the puzzle so others recognize it.",
      })
      return
    }

    let photoSignedId: string
    let journeyLogPhotoSignedId: string | null = null
    try {
      const puzzleUpload = await directImageUpload(photoFile)
      photoSignedId = puzzleUpload.signedId
      if (logPhotoFile) {
        const logUpload = await directImageUpload(logPhotoFile)
        journeyLogPhotoSignedId = logUpload.signedId
      }
    } catch {
      toast.error("Photo upload failed", {
        description: "Try a smaller image and try again.",
      })
      return
    }

    const result = await exec({
      variables: {
        input: {
          puzzleInput: {
            title: values.title.trim(),
            brand: values.brand.trim(),
            pieceCount: values.pieceCount,
          },
          journeyLogInput: {
            completedOn: values.completedOn,
            city: values.city?.trim() || null,
            state: values.state?.trim() || null,
            zipCode: values.zipCode?.trim() || null,
            comment: values.comment?.trim() || null,
            passedOn: values.passedOn,
            nextLocation: values.passedOn ? values.nextLocation?.trim() || null : null,
            displayAs: values.displayAs as JourneyLogDisplay,
          },
          photoSignedId,
          journeyLogPhotoSignedId,
        },
      },
    })

    if (result.error) {
      toast.error("Could not create puzzle", {
        description: "Please check the form and try again.",
      })
      return
    }

    const wpId = result.data?.puzzleCreate.puzzle.wpId
    if (!wpId) return

    toast.success("Puzzle registered!", {
      description: `Your puzzle is ${wpId}.`,
    })
    navigate(puzzleLabelPath({ wpId }))
  }

  const passedOn = form.watch("passedOn")
  const errors = form.formState.errors

  return (
    <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <Link to={dashboardPath({})} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to dashboard
      </Link>

      <header className="mt-4">
        <h1 className="text-2xl font-semibold">Register a new puzzle</h1>
        <p className="mt-2 text-sm text-gray-700">
          Adding a puzzle here is its first stop in the journey. Drop in a photo, where you are now,
          and a few words about the puzzle. Don&apos;t worry if you haven&apos;t finished it yet.
          Registering it kicks off the journey, and finders along the way will add their own stops.
        </p>
      </header>

      <form className="mt-8 space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <label className="text-base font-medium">
            Photo of the puzzle <span className="text-destructive">*</span>
          </label>
          <p className="mt-1 text-sm text-stone-600">
            A clear shot of the box top works best. This is how finders will recognize the puzzle.
          </p>
          <PhotoDropzone
            file={photoFile}
            onFileSelected={setPhotoFile}
            onRemove={() => setPhotoFile(null)}
            label="Puzzle photo"
            className="mt-3"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              {...form.register("title")}
              className={cn(inputClassName, "mt-1")}
              placeholder="Lighthouse at Dusk"
            />
            {errors.title ? (
              <p className="mt-1 text-xs text-destructive">{errors.title.message}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium">
              Brand <span className="text-destructive">*</span>
            </label>
            <input
              {...form.register("brand")}
              className={cn(inputClassName, "mt-1")}
              placeholder="Ravensburger"
            />
            {errors.brand ? (
              <p className="mt-1 text-xs text-destructive">{errors.brand.message}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium">
              Pieces <span className="text-destructive">*</span>
            </label>
            <input
              type="number"
              {...form.register("pieceCount", { valueAsNumber: true })}
              className={cn(inputClassName, "mt-1")}
              placeholder="1000"
              min={1}
              max={100000}
            />
            {errors.pieceCount ? (
              <p className="mt-1 text-xs text-destructive">{errors.pieceCount.message}</p>
            ) : null}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">
            Date <span className="text-destructive">*</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            When you finished it, or today if you haven&apos;t yet.
          </p>
          <input
            type="date"
            {...form.register("completedOn")}
            className={cn(inputClassName, "mt-1")}
          />
          {errors.completedOn ? (
            <p className="mt-1 text-xs text-destructive">{errors.completedOn.message}</p>
          ) : null}
        </div>

        <fieldset>
          <legend className="text-sm font-medium">
            Where is the puzzle starting its journey? <span className="text-destructive">*</span>
          </legend>
          <p className="mt-1 text-xs text-gray-500">
            Either a city/state or a zip code is fine. We use this to drop a pin on the
            puzzle&apos;s map, never your home address.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs text-gray-500">City</label>
              <input
                {...form.register("city")}
                className={cn(inputClassName, "mt-1")}
                placeholder="Brooklyn"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-xs text-gray-500">State</label>
              <input
                {...form.register("state")}
                className={cn(inputClassName, "mt-1")}
                placeholder="NY"
                maxLength={2}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500">Zip code (optional)</label>
              <input
                {...form.register("zipCode")}
                className={cn(inputClassName, "mt-1")}
                placeholder="11201"
                inputMode="numeric"
              />
            </div>
          </div>
          {errors.city ? (
            <p className="mt-1 text-xs text-destructive">{errors.city.message}</p>
          ) : null}
        </fieldset>

        <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 sm:p-5">
          <h2 className="text-base font-medium">Share your story (optional)</h2>
          <p className="mt-1 text-sm text-stone-600">
            Who did you do it with? What was your favorite bit? Stories like this are why people
            love seeing where these puzzles end up. Heads up: anything you write or upload here is
            public to anyone who scans the QR code, so leave out personal details you wouldn&apos;t
            want a stranger to see.
          </p>

          <div className="mt-4">
            <label className="text-sm font-medium">Your experience</label>
            <textarea
              {...form.register("comment")}
              className={cn(textareaClassName, "mt-1")}
              placeholder="The dusk gradient was tricky… we worked on this every weekend in October with my mom."
              maxLength={500}
            />
            {errors.comment ? (
              <p className="mt-1 text-xs text-destructive">{errors.comment.message}</p>
            ) : null}
          </div>

          <div className="mt-4">
            <label className="text-sm font-medium">A photo of you with it (optional)</label>
            <p className="mt-1 text-sm text-stone-600">
              Selfie with the puzzle, or a shot of the finished thing on your table. It&apos;ll
              appear on the map next to your stop.
            </p>
            <PhotoDropzone
              file={logPhotoFile}
              onFileSelected={setLogPhotoFile}
              onRemove={() => setLogPhotoFile(null)}
              label="Your journey photo"
              className="mt-3"
            />
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium">Did you pass it on?</legend>
          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" {...form.register("passedOn")} value="false" defaultChecked />
              No, still with me
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                {...form.register("passedOn", {
                  setValueAs: (v) => v === "true" || v === true,
                })}
                value="true"
              />
              Yes, sent it onward
            </label>
          </div>
          {passedOn ? (
            <input
              {...form.register("nextLocation")}
              className={cn(inputClassName, "mt-3")}
              placeholder="Where is it going? (e.g. 'mailing to my sister in Boise')"
            />
          ) : null}
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium">Show me as</legend>
          <div className="mt-2 flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" {...form.register("displayAs")} value="FIRST_NAME" />
              {viewer.firstName}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" {...form.register("displayAs")} value="ANONYMOUS" />
              Anonymous
            </label>
          </div>
        </fieldset>

        <div className="flex items-center gap-4 border-t pt-6">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Register puzzle"}
          </Button>
          <Link to={dashboardPath({})} className="text-sm text-gray-500">
            Cancel
          </Link>
        </div>
      </form>
    </main>
  )
}
